import {
  pllDefaultTeam,
  TEAM_LOGO_IMAGES,
  teamsDataArray,
  websiteUrls,
} from '../../Constants'
import { MAIN_PLL_URL } from '../../Config/config'
import type { TeamGQL, EventGQL, TeamStanding, TeamFrontData } from '../../Api'
import { SEASON_SEGMENT_NAMES } from '../seasonHelpers'
import {
  champSeriesTeams,
  fullNameTeamStatLabels,
  teamsBasicArray,
} from '../../Constants/team'
import { CURRENT_YEAR } from '../../Config/League/league.config'
import { SeasonType } from '../../Context/SeasonContext/SeasonContext'
import { TEAM_JERSEY_LINKS } from '../../Constants/merch'

export const teamStatCompareList = [
  'scoresPG',
  'saa',
  'scores',
  'onePointGoals',
  'shotPct',
  'shotsPG',
  'faceoffPct',
  'savePct',
]
export const previousMatchupList = [
  'scores',
  'saa',
  'onePointGoals',
  'shotPct',
  'faceoffPct',
  'savePct',
]

export function getIdByTeamName(teamName: string): string {
  const name = teamName.toLowerCase().trim()
  let tm: TeamFrontData | undefined
  if (name.includes('-')) {
    //with location
    tm = teamsDataArray.find((team) =>
      name?.includes(team.fullName.toLowerCase().trim())
    )
  } else if (name.length === 3) {
    //abbreviation ie ARC
    tm = teamsDataArray.find((team) => team.officialId.toLowerCase() === name)
  } else {
    //team name ie archers
    tm = teamsDataArray.find((team) => team.fullName.toLowerCase().trim() === name)
  }
  if (tm?.officialId) {
    return tm.officialId
  }
  throw new Error(`unknown team ${teamName}`)
}

export function getAdditionalTeamData(teamId: string): TeamFrontData {
  const team = teamsDataArray.find((td) => td.officialId === teamId)
  if (!team) {
    console.log(`unknown team ${teamId}`)
    return pllDefaultTeam
  }
  const result: TeamFrontData = {
    ...team,
    teamBanner: team.teamBanner,
    teamLogo: `${websiteUrls.teamBannerUrl}/${team.teamLogo}`,
    shopImg: `${websiteUrls.baseAssetUrl}/${team.shopImg}`,
    twitter: `https://twitter.com/${team.twTag}`,
    instagram: `https://www.instagram.com/${team.igTag}`,
  }
  return result
}

export function getFullTeamName(teamId: string): string | undefined {
  const team = teamsDataArray.find((td) => td.officialId === teamId)
  return team?.fullName
}

/**
 * Converts stat field to Human readable full stat name using `fullTeamNameStatLabels`
 */
export function mapTeamStat(stat: string): string {
  return fullNameTeamStatLabels[stat] || ''
}

export const getTeamColor = (teamId: string) => {
  const team = teamsDataArray.find((team) => team.officialId === teamId)
  if (!team) return '#000'
  return team.teamColor
}

export const getTeamColorByFullName = (teamName: string) => {
  const team = teamsDataArray.find(
    (team) => team.fullName.toLowerCase() === teamName.toLowerCase()
  )
  if (!team) return ''
  return team.teamColor
}

export const getTeamLogo = (teamId: string) => {
  const team = teamsBasicArray.find((team) => team.officialId === teamId)
  if (!team) return getGeneralTeamIc()
  return team.logo1 || getGeneralTeamIc()
}

export const getTeamLocation = (teamId: string) => {
  const team = teamsBasicArray.find((team) => team.officialId === teamId)
  if (!team) return
  return team.location
}

export const getTeamLocationCode = (teamId: string) => {
  const team = teamsBasicArray.find((team) => team.officialId === teamId)
  if (!team) return
  return team.locationCode
}

export const getTeamIc = (teamName: string) => {
  if (!teamName) {
    return getGeneralTeamIc()
  }
  if (teamName.length === 3) {
    //abbreviation like ARC
    const team = getFullTeamName(teamName)
    if (team) {
      teamName = team
    }
  }
  const lowerCase = teamName.toLowerCase()
  if (CURRENT_YEAR < 2024) {
    return `https://${MAIN_PLL_URL}/${lowerCase}_primary_fullcolor`
  }
  if (lowerCase in TEAM_LOGO_IMAGES) {
    return TEAM_LOGO_IMAGES[lowerCase]
  }

  return `https://${MAIN_PLL_URL}/wp-content/uploads/2024/03/shield-bg.png`
}

export function getTeamSeed(teamId: string): number | undefined {
  const team = teamsDataArray.find((td) => td.officialId === teamId)
  if (!team) return
  return
}

export const getGeneralTeamIc = () => {
  return `https://${MAIN_PLL_URL}/wp-content/uploads/2024/03/shield-bg.png`
}

export const getTeamBettingIc = (teamName: string) => {
  const lowerCase = teamName.toLowerCase()
  if (!teamName) {
    return getGeneralTeamIc()
  }
  return `https://${MAIN_PLL_URL}/${lowerCase}bet`
}

export function getTeamRecord(
  team: TeamGQL,
  segment: SeasonType = SEASON_SEGMENT_NAMES.regular
): string | undefined {
  if (!team) {
    console.warn('getTeamRecord: No team provided')
    return ''
  }

  //REST routes with wins already calculated
  if(team?.wins){
    return `${team.wins ?? 0} - ${team?.losses ?? 0} ${
      team?.ties && team?.ties > 0 ? `- ${team?.ties}` : ''
    }`
  }

  //Graphql routes with wins not calculated
  const recordCalculators = {
    [SEASON_SEGMENT_NAMES.regular]: {
      wins: team.teamWins ?? 0,
      losses: team.teamLosses ?? 0,
      ties: team.teamTies ?? 0,
    },
    [SEASON_SEGMENT_NAMES.post]: {
      wins: (team.teamWins ?? 0) + (team.teamWinsPost ?? 0),
      losses: (team.teamLosses ?? 0) + (team.teamLossesPost ?? 0),
      ties: (team.teamTies ?? 0) + (team.teamTiesPost ?? 0),
    },
    [SEASON_SEGMENT_NAMES.champseries]: {
      wins: team.champSeries?.teamWins ?? 0,
      losses: team.champSeries?.teamLosses ?? 0,
      ties: team.champSeries?.teamTies ?? 0,
    },
  }

  if (!Object.prototype.hasOwnProperty.call(recordCalculators, segment)) return ''
  const record = recordCalculators[segment]
  const { wins = 0, losses = 0, ties = 0 } = record
  return `${wins}-${losses}${ties > 0 ? `-${ties}` : ''}`
}

export const getTeamJerseyData = (teamName: string | undefined) => {
  if(!teamName) return ''
  const lowerCase = teamName.toLowerCase()
  if(!(lowerCase in TEAM_JERSEY_LINKS)) return ''
  return TEAM_JERSEY_LINKS[lowerCase]
}

export const getTeamEvents = (events: EventGQL[], team: string): EventGQL[] => {
  const teamEvents = events.filter(
    (event) => event.homeTeam?.officialId === team || event.awayTeam?.officialId === team
  )
  return teamEvents
}

export const filterChampSeriesTeams = (teams: TeamGQL[], year: number) => {
  let csTeams = champSeriesTeams[year]
  if (!csTeams || csTeams.length < 1) return []
  let teamList = teams.filter((tm) => isChampSeriesTeam(tm, csTeams))
  return teamList
}

export const filterChampSeriesStandings = (teams: TeamStanding[], year: number) => {
  let csTeams = champSeriesTeams[year]
  if (!csTeams || csTeams.length < 1) return []
  let teamList = teams.filter((tm) => isChampSeriesTeam(tm.team, csTeams))
  return teamList
}

export const isChampSeriesTeam = (team: TeamGQL, csTeams: string[]) => {
  let tm = csTeams.find((tm) => tm === team.officialId.toLowerCase())
  if (!tm) return
  return team
}
